'use client'

import { motion } from 'framer-motion'
import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'
import { useMemo, useRef, useState } from 'react'
import { useDraggable } from 'react-use-draggable-scroll'

import { useReplaceQuery } from '@/util/router'

export type TabItem = {
  id: number | string
  text?: string
  icon?: React.ReactNode
}

type Props = {
  id: string
  tabs: TabItem[]
  useSearchParam?: boolean
  resetQueries?: string[]
  onChange?: (id: string | number) => void
  manipulateQuery?: (query: Record<string, string>) => Record<string, string>
}

export const Tabs = ({
  tabs,
  id,
  useSearchParam,
  resetQueries,
  onChange,
  manipulateQuery,
}: Props) => {
  const searchParams = useSearchParams()
  const { replaceQuery, query, replaceQueries } = useReplaceQuery()

  const [_index, _setIndex] = useState(tabs[0].id)

  const index = useMemo(() => {
    if (useSearchParam) {
      return searchParams.get(id) || tabs[0].id
    }

    return _index
  }, [useSearchParam, searchParams, _index, id, tabs])

  const onChangeTab = (tab: TabItem) => {
    if (useSearchParam) {
      if (manipulateQuery) {
        replaceQueries(manipulateQuery({ ...query, [id]: tab.id.toString() }))
      } else {
        replaceQuery(id, tab.id.toString(), { resetQueries })
      }
    } else {
      _setIndex(tab.id)
    }

    onChange?.(tab.id)
  }

  const ref = useRef<HTMLDivElement>(
    null,
  ) as React.MutableRefObject<HTMLDivElement>
  const { events } = useDraggable(ref)

  return (
    <nav ref={ref} className="overflow-auto scrollbar-hide" {...events}>
      <motion.ul
        className="bg-gray-50 border border-gray-50 h-12 flex rounded-xl min-w-min"
        layout
      >
        {tabs.map((tab) => {
          return (
            <li
              key={tab.id}
              className="w-max grow px-4 flex justify-center items-center rounded-xl cursor-pointer gap-0.5 relative"
              onClick={() => onChangeTab(tab)}
            >
              <div
                className={classNames(
                  'z-10 flex justify-center items-center transition-opacity duration-500 select-none',
                  {
                    'opacity-60': index !== tab.id,
                  },
                )}
              >
                {tab.icon}
                {tab.text && (
                  <span className="text-body1 font-medium whitespace-nowrap">
                    {tab.text}
                  </span>
                )}
              </div>
              {index === tab.id && (
                <motion.div
                  className="w-full h-full absolute left-0 p-0.5 pointer-events-none"
                  layoutId={`tab-${id}`}
                >
                  <div className="bg-dialog h-full w-full rounded-xl" />
                </motion.div>
              )}
            </li>
          )
        })}
      </motion.ul>
    </nav>
  )
}
